import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { BookPlus, ArrowRight } from 'lucide-react'
import Button from '@/components/ui/Button'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/lib/auth-context'
import { supabase } from '@/lib/supabase'

interface OutletContext {
  currentStep: number
  goToStep: (index: number) => void
  handleForward: () => void
  handleBack: () => void
}

export default function ShareScreen() {
  const { t } = useTranslation()
  const { handleForward } = useOutletContext<OutletContext>()
  const navigate = useNavigate()
  const { user, profile, refreshProfile } = useAuth()
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100)
    return () => clearTimeout(t)
  }, [])

  // Leaving onboarding early via "Add a book" must still mark the user as
  // onboarded (same pattern as DoneScreen), otherwise AuthGate treats them
  // as a new user and loops them back into onboarding.
  const handleShareBook = useCallback(async () => {
    if (user?.id && !profile?.onboarded_at) {
      // Start the 30-day trial too — the book path used to skip this, so
      // these users stayed on "free". Idempotent server-side (only sets
      // trial_ends_at when NULL), so it's safe alongside DoneScreen. Kept in
      // its own try/catch: a trial failure must not break the flow below.
      if (!profile?.trial_ends_at) {
        try {
          await supabase.rpc('start_trial', { p_user_id: user.id })
        } catch (e) {
          console.warn('Could not start trial:', e)
        }
      }
      try {
        await supabase
          .from('profiles')
          .update({ onboarded_at: new Date().toISOString() })
          .eq('id', user.id)
          .is('onboarded_at', null) // don't overwrite an existing timestamp
        await refreshProfile()
      } catch (e) {
        console.warn('Could not complete onboarding:', e)
      }
    }
    navigate('/books/add')
  }, [user?.id, profile?.onboarded_at, profile?.trial_ends_at, refreshProfile, navigate])

  const handleLater = useCallback(() => {
    handleForward()
  }, [handleForward])

  return (
    <div className="flex flex-col py-8 min-h-[calc(100vh-200px)]">
      {/* Title */}
      <h1
        className={`font-display text-3xl text-ink mb-4 transition-all duration-700 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
        }`}
      >
        {t('onboarding.share_title')}
      </h1>

      {/* Body text */}
      <p
        className={`font-body text-ink-muted leading-relaxed mb-10 transition-all duration-700 delay-100 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
        }`}
      >
        {t('onboarding.share_body')}
      </p>

      {/* Decorative book illustration */}
      <div
        className={`flex justify-center mb-10 transition-all duration-700 delay-200 ${
          visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}
      >
        <div className="w-32 h-40 bg-green/10 rounded-warm flex items-center justify-center">
          <BookPlus className="w-12 h-12 text-green/40" />
        </div>
      </div>

      {/* Action buttons */}
      <div
        className={`flex flex-col gap-3 transition-all duration-700 delay-300 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
        }`}
      >
        <Button
          onClick={handleShareBook}
          className="w-full h-14 btn-primary flex items-center justify-center gap-2"
        >
          <BookPlus className="w-5 h-5" />
          {t('onboarding.share_add_book')}
          <ArrowRight className="w-4 h-4" />
        </Button>

        <Button
          variant="ghost"
          onClick={handleLater}
          className="w-full h-12 text-ink-muted hover:text-ink font-body"
        >
          {t('onboarding.share_later')}
        </Button>
      </div>

      {/* Warm, quiet reassurance */}
      <p
        className={`mt-auto pt-8 text-center text-xs text-ink-muted/50 font-body italic transition-all duration-700 delay-400 ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {t('onboarding.share_reassurance')}
      </p>
    </div>
  )
}
